import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

type ClientLite = { id: string; name: string | null; website_url: string | null };

type PlannedLink = { url: string; anchor: string };
type ValidatorError = { code: string; field: string; message: string; severity: "error" | "warn" };
type Stage = { name: string; ok: boolean; detail?: any };

type DryRunResult = {
  dry_run: boolean;
  production_equivalent?: boolean;
  client_id: string;
  client_name?: string;
  website_url?: string;
  business_name?: string;
  keyword: string;
  language: "en" | "fr";
  
  title?: string;
  meta_description?: string;
  slug?: string;
  canonical_url?: string;
  raw_writer_html?: string;
  final_html: string;
  link_plan?: PlannedLink[];
  links_inserted: { anchor: string; href: string; type: "brand" | "topical" }[];
  link_count: number;
  links_skipped?: { url: string; reason: string }[];
  domain_stripped?: string[];
  whitelist_stripped?: any[];
  image_dedupe_removed?: number;
  validator_errors?: ValidatorError[];
  validator_summary?: { errors: number; warnings: number; error_codes: string[]; warning_codes: string[] };
  link_probes?: Record<string, { status: number; ok: boolean; error?: string }>;
  route_probe?: { url: string; status: number; ok: boolean } | null;
  would_publish?: boolean;
  stages?: Stage[];
};

export default function DryRunPanel() {
  const [clients, setClients] = useState<ClientLite[]>([]);
  const [clientId, setClientId] = useState<string>("");
  const [keyword, setKeyword] = useState<string>("");
  const [language, setLanguage] = useState<"en" | "fr">("en");
  
  const [withImages, setWithImages] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<DryRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      const { data, error } = await supabase.functions.invoke("admin-list-clients");
      if (error) {
        toast({ title: "Failed to load clients", description: error.message, variant: "destructive" });
        return;
      }
      const list: ClientLite[] = (data?.clients ?? []).map((c: any) => ({
        id: c.id, name: c.name, website_url: c.website_url,
      }));
      setClients(list);
      if (list.length && !clientId) setClientId(list[0].id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const run = async () => {
    if (!clientId || !keyword.trim()) {
      toast({ title: "Missing input", description: "Pick a client and enter a keyword.", variant: "destructive" });
      return;
    }
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("generate-first-articles", {
        body: { dry_run: true, client_id: clientId, keyword: keyword.trim(), language, with_images: withImages },
      });
      if (error) throw new Error(error.message);
      if ((data as any)?.error) throw new Error((data as any).error);
      setResult(data as DryRunResult);
    } catch (e) {
      setError((e as Error).message);
      toast({ title: "Dry-run failed", description: (e as Error).message, variant: "destructive" });
    } finally {
      setRunning(false);
    }
  };

  const sortedClients = useMemo(
    () => [...clients].sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [clients],
  );

  return (
    <div className="mb-8 rounded-[14px] border border-border bg-card p-5">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Production-equivalent dry-run</h2>
          <p className="text-xs text-muted-foreground mt-1">
            Executes the EXACT production pipeline (planner → writer → guards → validators → probes). No DB write, no CMS push, no public visibility. If production would fail or strip a link, dry-run does the same.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_1fr_110px_130px_auto]">
        <select
          value={clientId}
          onChange={(e) => setClientId(e.target.value)}
          className="h-10 rounded-[10px] border border-border bg-background px-3 text-sm"
        >
          {sortedClients.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name || c.id.slice(0, 8)} {c.website_url ? `— ${c.website_url}` : ""}
            </option>
          ))}
        </select>
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="Test keyword (e.g. emergency lock change)"
          className="h-10 rounded-[10px] border border-border bg-background px-3 text-sm"
        />
        <select
          value={language}
          onChange={(e) => setLanguage(e.target.value as "en" | "fr")}
          className="h-10 rounded-[10px] border border-border bg-background px-3 text-sm"
        >
          <option value="en">English</option>
          <option value="fr">Français</option>
        </select>
        <label className="flex h-10 items-center gap-2 rounded-[10px] border border-border bg-background px-3 text-sm">
          <input type="checkbox" checked={withImages} onChange={(e) => setWithImages(e.target.checked)} />
          With images
        </label>
        <button
          onClick={run}
          disabled={running}
          className="h-10 rounded-[10px] bg-primary px-5 text-sm font-semibold text-primary-foreground disabled:opacity-60"
        >
          {running ? "Running…" : "Run dry-run"}
        </button>
      </div>

      {error && (
        <div className="mt-4 rounded-[10px] border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {result && (
        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <div className={`rounded-[10px] border p-4 lg:col-span-2 ${result.would_publish ? "border-emerald-500/40 bg-emerald-500/10" : "border-destructive/40 bg-destructive/10"}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="text-sm font-semibold">
                {result.would_publish ? "✓ Would PUBLISH in production" : "✗ Would FAIL in production"}
              </div>
              <div className="text-xs text-muted-foreground">
                slug: <span className="font-mono">{result.slug}</span> · canonical: <span className="font-mono break-all">{result.canonical_url}</span>
              </div>
            </div>
            {result.validator_summary && (
              <div className="mt-2 text-xs">
                {result.validator_summary.errors} error(s), {result.validator_summary.warnings} warning(s)
                {result.route_probe && (
                  <span className="ml-3">
                    route probe: <span className="font-mono">{result.route_probe.status}</span> {result.route_probe.ok ? "✓" : "✗"}
                  </span>
                )}
              </div>
            )}
          </div>

          {result.stages && result.stages.length > 0 && (
            <div className="rounded-[10px] border border-border p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold mb-2">Pipeline stages</h3>
              <ol className="space-y-1 text-xs">
                {result.stages.map((s, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className={`mt-0.5 inline-block h-2 w-2 rounded-full ${s.ok ? "bg-emerald-500" : "bg-destructive"}`} />
                    <span className="font-mono">{s.name}</span>
                    {s.detail !== undefined && (
                      <span className="text-muted-foreground break-all">— {typeof s.detail === "object" ? JSON.stringify(s.detail).slice(0, 220) : String(s.detail)}</span>
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {result.validator_errors && result.validator_errors.length > 0 && (
            <div className="rounded-[10px] border border-border p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold mb-2">Validator findings ({result.validator_errors.length})</h3>
              <ul className="space-y-1 text-xs">
                {result.validator_errors.map((e, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] uppercase font-bold ${e.severity === "error" ? "bg-destructive/20 text-destructive" : "bg-amber-500/15 text-amber-700"}`}>
                      {e.severity}
                    </span>
                    <span className="font-mono">{e.code}</span>
                    <span className="text-muted-foreground">— {e.message}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {((result.domain_stripped && result.domain_stripped.length > 0) || (result.whitelist_stripped && result.whitelist_stripped.length > 0)) && (
            <div className="rounded-[10px] border border-border p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold mb-2">Links stripped during production guards</h3>
              {result.domain_stripped && result.domain_stripped.length > 0 && (
                <div className="mb-2 text-xs">
                  <div className="font-semibold mb-1">Wrong-domain ({result.domain_stripped.length}):</div>
                  <ul className="font-mono">{result.domain_stripped.map((u, i) => <li key={i} className="break-all">{u}</li>)}</ul>
                </div>
              )}
              {result.whitelist_stripped && result.whitelist_stripped.length > 0 && (
                <div className="text-xs">
                  <div className="font-semibold mb-1">Whitelist sweep ({result.whitelist_stripped.length}):</div>
                  <ul className="font-mono">{result.whitelist_stripped.map((u, i) => <li key={i} className="break-all">{JSON.stringify(u)}</li>)}</ul>
                </div>
              )}
            </div>
          )}

          {result.link_probes && Object.keys(result.link_probes).length > 0 && (
            <div className="rounded-[10px] border border-border p-4 lg:col-span-2">
              <h3 className="text-sm font-semibold mb-2">Internal link probes</h3>
              <ul className="space-y-1 text-xs">
                {Object.entries(result.link_probes).map(([url, p]) => (
                  <li key={url} className="flex items-start gap-2">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-bold ${p.ok ? "bg-emerald-500/15 text-emerald-700" : "bg-destructive/20 text-destructive"}`}>{p.status || "ERR"}</span>
                    <span className="font-mono break-all">{url}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="rounded-[10px] border border-border p-4">
            <h3 className="text-sm font-semibold mb-2">
              Link plan ({(result.link_plan || []).length})
            </h3>
            {(result.link_plan || []).length === 0 ? (
              <div className="text-xs text-muted-foreground">No links planned.</div>
            ) : (
              <ul className="space-y-2">
                {(result.link_plan || []).map((l, i) => (
                  <li key={i} className="text-xs">
                    <span className="font-mono">"{l.anchor}"</span>
                    <span className="text-muted-foreground"> → </span>
                    <a href={l.url} target="_blank" rel="noreferrer" className="text-primary underline break-all">
                      {l.url}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-[10px] border border-border p-4">
            <h3 className="text-sm font-semibold mb-2">
              Links inserted ({result.link_count})
            </h3>
            {result.links_inserted.length === 0 ? (
              <div className="text-xs text-muted-foreground">No links inserted.</div>
            ) : (
              <ul className="space-y-2">
                {result.links_inserted.map((l, i) => (
                  <li key={i} className="text-xs">
                    <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] uppercase font-bold ${
                      l.type === "brand" ? "bg-primary/20 text-primary" : "bg-muted text-foreground"
                    }`}>
                      {l.type}
                    </span>
                    <span className="font-mono">"{l.anchor}"</span>
                    <span className="text-muted-foreground"> → </span>
                    <a href={l.href} target="_blank" rel="noreferrer" className="text-primary underline break-all">
                      {l.href}
                    </a>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-[10px] border border-border p-4 lg:col-span-2">
            <h3 className="text-sm font-semibold mb-2">
              Phrases skipped ({(result.links_skipped || []).length})
            </h3>
            {(result.links_skipped || []).length === 0 ? (
              <div className="text-xs text-muted-foreground">All planned phrases were wrapped.</div>
            ) : (
              <ul className="space-y-1.5">
                {(result.links_skipped || []).map((s, i) => (
                  <li key={i} className="text-xs flex items-start gap-2">
                    <span className="rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-amber-700">skip</span>
                    <span className="break-all flex-1">
                      <span className="font-mono">{s.url}</span>
                      <span className="text-muted-foreground"> — {s.reason}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="rounded-[10px] border border-border p-4 lg:col-span-2">
            <div className="mb-2 flex items-center justify-between">
              <h3 className="text-sm font-semibold">Final HTML preview</h3>
              <span className="text-xs text-muted-foreground">
                {result.title} · {result.language.toUpperCase()}
              </span>
            </div>
            <div
              className="prose prose-sm max-w-none rounded border border-border bg-background p-4 max-h-[500px] overflow-auto"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: result.final_html }}
            />
          </div>

          <details className="rounded-[10px] border border-border p-4 lg:col-span-2">
            <summary className="text-sm font-semibold cursor-pointer">Raw JSON response</summary>
            <pre className="mt-3 overflow-auto text-[11px] leading-snug">
              {JSON.stringify(result, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </div>
  );
}
